<script setup lang="ts">
import {
  faBold,
  faItalic,
  faStrikethrough,
  faSubscript,
  faSuperscript,
  faUnderline,
  faEllipsisV,
  faLink,
  type IconDefinition
} from "@fortawesome/pro-light-svg-icons";
import { MailMenuColor, MailMenuLink } from "#components";
import type { Editor } from "@tiptap/vue-3";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";

export interface MenuGroupItem {
  label?: string
  icon?: IconDefinition,
  action?: () => unknown | Promise<unknown>
  isActive?: boolean,
  menu?: MenuGroup[]
  component?: any
}

export type Menu = MenuGroup[];
type MenuGroup = MenuGroupItem[];

const { editor = undefined } = defineProps<{
  editor?: Editor
}>();

const store = useMailStore();

const menu = computed<Menu>(() => {
  const groups: MenuGroup[] = [];

  const pos = {
    from: (editor ?? store.editor)?.state.selection.from,
    to: (editor ?? store.editor)?.state.selection.to,
  };

  groups.push([
    { icon: faBold, action: () => (editor ?? store.editor)?.chain().focus().toggleBold().run(), isActive: (editor ?? store.editor)?.isActive('bold') },
    {
      icon: faUnderline,
      action: () => (editor ?? store.editor)?.chain().focus().toggleUnderline().run(),
      isActive: (editor ?? store.editor)?.isActive('underline')
    },
    {
      icon: faItalic,
      action: () => (editor ?? store.editor)?.chain().focus().toggleItalic().run(),
      isActive: (editor ?? store.editor)?.isActive('italic')
    },
    {
      icon: faStrikethrough,
      action: () => (editor ?? store.editor)?.chain().focus().toggleStrike().run(),
      isActive: (editor ?? store.editor)?.isActive('strike')
    },
    {
      icon: faCircle,
      isActive: (editor ?? store.editor)?.isActive('textStyle'),
      component: MailMenuColor,
    },
    {
      icon: faLink,
      isActive: (editor ?? store.editor)?.isActive('link'),
      component: MailMenuLink
    },
    {
      icon: faEllipsisV,
      menu: [
        [
          {
            icon: faSubscript,
            action: () => (editor ?? store.editor)?.chain().focus().toggleSubscript().run(),
            isActive: (editor ?? store.editor)?.isActive('subscript')
          },
          {
            icon: faSuperscript,
            action: () => (editor ?? store.editor)?.chain().focus().toggleSuperscript().run(),
            isActive: (editor ?? store.editor)?.isActive('superscript')
          }
        ]
      ]
    },
  ])

  return groups;
})

const emit = defineEmits(['action']);

function onAction() {
  if (!editor) {
    store.blockUpdate = true;
  } else {
    emit('action')
  }
}
</script>

<template>
  <MailEditorMenuGroups @action="onAction" :fields="store.builderFields" :menu="menu" :editor="store.editor" />
</template>

<style scoped></style>